import { Inject, Injectable, Optional } from '@angular/core';
import { urls } from '@app/shared/utils';
import { ApmService } from '@elastic/apm-rum-angular';
import { ChronosSharedConfiguration, ApiAuthorizationConfig, LiveUrlConfig } from 'chronos-shared';
import { ApmConfig, AuthConfig, FrontendConfig, WebSocketClientService } from 'chronos-core-client';
import { BehaviorSubject, Observable } from 'rxjs';
import moment from 'moment';
import 'moment-timezone';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipeConfig } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  public loadingIndicatorSubject = new BehaviorSubject<boolean>(false);
  public loadingIndicatorSubject$: Observable<boolean> = this.loadingIndicatorSubject.asObservable();

  constructor(
    private chronosSharedConfiguration: ChronosSharedConfiguration,
    private authConfig: ApiAuthorizationConfig,
    private liveUrlConfig: LiveUrlConfig,
    private webSocketClientService: WebSocketClientService,
    private apmService: ApmService,
    @Inject(DATE_PIPE_DEFAULT_OPTIONS) private datePipeConfig: DatePipeConfig
  ) {}

  public async Init(): Promise<any> {
    // deliberately avoid using Angular HTTP here to not trigger any implicit default configurations
    const frontendConfigResponse = await fetch(urls.getFrontendConfig());

    // when the core service is down, the frontend configuration cannot be fetched
    // give a message to the user to inform about the issue
    if (frontendConfigResponse.status !== 200) {
      document.children[0].innerHTML =
        '<div style="display: grid">' +
        '  <span>Failed to load frontend configuration</span>' +
        '  <span>Please reload the page later!</span>' +
        '  <span>&nbsp;</span>' +
        `  <span>${frontendConfigResponse.status} ${frontendConfigResponse.statusText}</span>` +
        `  <span>URL: ${frontendConfigResponse.url}'</span>` +
        '</div>';

      // reload page every minute to check if the service is back online
      setTimeout(() => {
        window.location.reload();
      }, 60000);
    }

    const frontendConfig: FrontendConfig = await frontendConfigResponse.json();

    // initialize APM first to ensure all errors are properly reported
    this.initializeApm(frontendConfig.apm);
    this.initializeAuthentication(frontendConfig.authentication);
    this.initializeUrls(frontendConfig);
    this.initializeTimeZone(frontendConfig.timeZone);

    await this.webSocketClientService.startConnection();
  }

  private initializeTimeZone(timeZone: string | null): void {
    if (!timeZone) {
      return;
    }

    moment.tz.setDefault(timeZone);
    this.datePipeConfig.timezone = timeZone;
  }

  private initializeUrls(frontendConfig: FrontendConfig): void {
    if (frontendConfig.pandaRootUrl) {
      this.chronosSharedConfiguration.pandaRootUrl = frontendConfig.pandaRootUrl.replace(/\/$/, '');
    }

    if (frontendConfig?.chronosLiveFrontendUrl) {
      this.liveUrlConfig.url = frontendConfig.chronosLiveFrontendUrl.replace(/\/$/, '');
    }
  }

  private initializeApm(apmConfig: ApmConfig): void {
    const defaults = {
      enabled: false,
      serviceName: 'chronos-cockpit',
      logLevel: 'warn' as LogLevel
    };
    const config = { ...defaults, ...apmConfig };

    if (config.enabled) {
      this.apmService.init(config);
    }
  }

  private initializeAuthentication(fetchedAuthConfig: AuthConfig | null): void {
    if (!fetchedAuthConfig) {
      return;
    }
    this.authConfig.authority = fetchedAuthConfig.authority ?? this.authConfig.authority;
    this.authConfig.clientId = fetchedAuthConfig.clientId ?? this.authConfig.clientId;
    this.authConfig.responseType = fetchedAuthConfig.responseType ?? this.authConfig.responseType;
    this.authConfig.scope = fetchedAuthConfig.scope ?? this.authConfig.scope;
  }
}
